import { useGetPayrollRGD } from 'hooks/assets-protection/usePayRollRGD'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useCallback } from 'react'
import {
    getGreenfieldDrillValues,
    getGreenfieldLocationFilter,
} from 'utils/greenfield'
import { addHyperlinkToFirstColumn, pickColumns } from '../helpers'
import { GeneralizedComponent } from 'components/generalized-component/generalized-component'
import { Container } from 'components/container/Container'
import { ErrorAlert } from 'components/error-fallback'
import { BasicPlaceholder } from 'components/placeholder'
import useCountdown from 'hooks/utils/useCountdown'
import { translateTableData } from 'locales/utils'
import { useAssetsProtectionContext } from '../assets-protection-context'

export const PayrollRGD = () => {
    const { t, currentLocation, env, session, logout } = useEssentials()
    const { activePayrollTab, setActivePayrollTab } =
        useAssetsProtectionContext()

    const { assetsProtection } = TRANSLATION_KEYS
    const { headers, payroll } = assetsProtection
    const { filters } = assetsProtection

    const commonFilters = {
        ...getGreenfieldDrillValues(currentLocation),
        ...getGreenfieldLocationFilter(currentLocation),
        prepend_footer_to_rows: 'true',
    }

    const {
        data: priorMonthData,
        isLoading: isPriorMonthLoading,
        isError: isPriorMonthError,
        error,
        refetch: refetchPriorMonth,
    } = useGetPayrollRGD({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        filters: commonFilters,
        timeDimension: 'prior_month',
    })

    const {
        data: ytdData,
        isLoading: isYtdLoading,
        isError: isYtdError,
        error: ytdError,
        refetch: refetchYtd,
    } = useGetPayrollRGD({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        filters: commonFilters,
        timeDimension: 'year_to_day',
    })
    const ytd = t(assetsProtection.yearToDate)
    const priorMonth = t(assetsProtection.priorMonth)
    const payrollRGDHeaders = {
        ...headers.payrollRGDMonthly,
        'b/(p) % to Plan': payroll.payrollBPLYLabel,
    }
    const payload =
        ytdData && priorMonthData
            ? {
                  [t(filters.viewInDollars)]: [
                      {
                          title: '',
                          subtitle: '',
                          data: {
                              [priorMonth]: addHyperlinkToFirstColumn(
                                  translateTableData({
                                      t,
                                      table: pickColumns(
                                          priorMonthData,
                                          [0, 1, 2, 6, 5],
                                      ),
                                      translation: payrollRGDHeaders,
                                  }),
                                  currentLocation,
                              ),
                              [ytd]: addHyperlinkToFirstColumn(
                                  translateTableData({
                                      t,
                                      table: pickColumns(
                                          ytdData,
                                          [0, 1, 2, 6, 5],
                                      ),
                                      translation: payrollRGDHeaders,
                                  }),
                                  currentLocation,
                              ),
                          },
                      },
                  ],
              }
            : { [t(filters.viewInDollars)]: [] }

    const handleClick = useCallback(
        (timePeriodIndex: number, tabsIndex: number) => {
            setActivePayrollTab(tabsIndex)
        },
        [setActivePayrollTab],
    )
    const count = useCountdown(5, logout, error as Error)

    if (isPriorMonthLoading || isYtdLoading) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="table" rows={5} columns={5} />
            </div>
        )
    }
    if (isYtdError) {
        return (
            <ErrorAlert
                error={ytdError}
                count={count}
                onRetry={() => refetchYtd()}
            />
        )
    }
    if (isPriorMonthError) {
        return (
            <ErrorAlert
                error={error}
                count={count}
                onRetry={() => refetchPriorMonth()}
            />
        )
    }
    return (
        <Container role="main">
            <GeneralizedComponent
                payload={payload}
                isLoading={isPriorMonthLoading || isYtdLoading}
                scrollableWidth={true}
                widths={[70, 70, 80, 80, 80]}
                parentHandleClick={handleClick}
                selectedTabIndex={activePayrollTab}
                kpiName="assets protection payroll non-store"
            />
        </Container>
    )
}
